import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  RiCheckboxBlankCircleLine,
  RiRecordCircleFill,
  RiCheckboxBlankLine,
  RiCheckboxFill,
} from 'react-icons/ri';

import {
  OrderingSelectors,
  OrderingConstants,
  OrderingOperations,
  OrderingPaymentHooks,
} from 'polygon-ordering';

import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import Text from './Text';
import TouchableOpacity from './TouchableOpacity';
import { paymentInfo } from '../utils/analytics';
import { useAppSelector, useAppDispatch } from '../app/hooks';

import lodash from 'lodash';
import { PAYMENT_METHOD } from '../libs/polygon-ordering/src/constants/paymentMethod';

const { updateSelectedPaymentMethods } = OrderingOperations;

const {
  getAvailablePaymentMethods,
  getEnabledPaymentMethods,
  getSaleType,
  getSelectedPaymentMethods,
  getTotalCoveredBySubPayments,
  getOrderTotals,
  getStagedPurchases,
} = OrderingSelectors;

const { PAYMENT_GATEWAY_PAYMENT_METHODS, MIN_CC_AMOUNT_NOT_MET } = OrderingConstants;

interface IProps {
  disabled?: boolean;
  children?: React.ReactNode;
  selected?: () => boolean;
  alreadySelected?: boolean;
  allowSelectionWhenUnavailable?: boolean;
  additionalBehaviour?: () => void;
  right?: React.ReactNode | null;
  subtitle?: React.ReactNode | null;
  label?: string;
  method: PAYMENT_METHOD;
}

const PaymentMethod: React.FC<IProps> = ({
  method,
  disabled,
  label,
  right,
  children,
  subtitle,
  additionalBehaviour,
  allowSelectionWhenUnavailable = false,
  selected,
  alreadySelected,
}) => {
  const p = useAppSelector(getThemeLookup);
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const saleType = useAppSelector(getSaleType);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const totalCoveredBySubPayments = useAppSelector(getTotalCoveredBySubPayments);
  const availablePaymentMethods = useAppSelector(getAvailablePaymentMethods);
  const enabledPaymentMethods = useAppSelector(getEnabledPaymentMethods);
  const orderTotals = useAppSelector(getOrderTotals);
  const stagedPurchases = useAppSelector(getStagedPurchases);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  if (!enabledPaymentMethods.includes(method)) {
    return null;
  }

  //@ts-ignore
  OrderingPaymentHooks.subscribe(MIN_CC_AMOUNT_NOT_MET, () => {
    enqueueErrorSnackbar(t('creditCardMinimumNotMetMessage'), { persist: true });
  });

  alreadySelected = !selected
    ? Boolean(lodash.find(selectedPaymentMethods, ['method', method]))
    : selected();

  const notAvailable = !availablePaymentMethods.includes(method);

  const totalCoveredAndNotSelected = totalCoveredBySubPayments && !alreadySelected;

  // Checks if no member methods have been selected
  const disabledByOtherSelections = !selectedPaymentMethods.some(selectedPaymentMethod => {
    if (!PAYMENT_GATEWAY_PAYMENT_METHODS.includes(selectedPaymentMethod.method)) {
      return true;
    }
    return false;
  })
    ? false
    : totalCoveredAndNotSelected;

  const IconComponent =
    PAYMENT_GATEWAY_PAYMENT_METHODS.includes(method) ||
    method === PAYMENT_METHOD.PAY_LATER ||
    method === PAYMENT_METHOD.MEMBER_POINTS ||
    method === PAYMENT_METHOD.MEMBER_REWARDS
      ? alreadySelected
        ? RiRecordCircleFill
        : RiCheckboxBlankCircleLine
      : alreadySelected
      ? RiCheckboxFill
      : RiCheckboxBlankLine;

  return (
    <TouchableOpacity
      style={styles.mainContainer}
      onClick={e => {
        if (!alreadySelected) {
          paymentInfo(
            stagedPurchases.map(purchaseItem => {
              return {
                ...purchaseItem.item,
                totalPrice: purchaseItem.moneyPrice,
              };
            }),
            orderTotals!.discountedMoneyPrice,
            method,
          );
        }

        if ((e?.target as HTMLDivElement).tagName === 'INPUT') {
          if (!alreadySelected) {
            dispatch(updateSelectedPaymentMethods({ paymentMethod: method, alreadySelected }));
          }
        } else {
          dispatch(updateSelectedPaymentMethods({ paymentMethod: method, alreadySelected }));
        }
        if (additionalBehaviour) {
          additionalBehaviour();
        }
      }}
      disabled={
        checkoutInProgress ||
        disabled ||
        (notAvailable && !allowSelectionWhenUnavailable) ||
        disabledByOtherSelections
      }
      disabledStyle={styles.disabled}
      enabledDefault
      skipMouseEvent
      ariaLabel={label}
    >
      <div style={styles.rightContainer}>
        <div style={styles.topContainer}>
          <IconComponent style={combineStyles(p('paymentMethodIcon', ['color', 'fontSize']))} />

          {method === PAYMENT_METHOD.PAY_LATER ? (
            <Text themeKey="paymentMethod" style={{ marginRight: 'auto', paddingLeft: 3 }}>
              {label || t(`paymentMethod.${method}.${saleType}`)}
            </Text>
          ) : (
            <Text style={{ marginRight: 'auto', paddingLeft: 3 }} themeKey="paymentMethod">
              {label || t(`paymentMethod.${method}`)}
            </Text>
          )}
          {right == null ? <div /> : right}
        </div>

        <div>{subtitle}</div>

        <div>{alreadySelected ? children : undefined}</div>
      </div>
    </TouchableOpacity>
  );
};

const styles: Styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },

  icon: {
    margin: '3px 10px 0 0',
  },

  rightContainer: {
    flex: 1,
  },

  topContainer: {
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  disabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
  },
};

export default PaymentMethod;
